import { useCallback } from 'react';
import {
  useSearchPeopleLazyQuery,
  useSearchCompaniesLazyQuery,
  useSearchDealsLazyQuery,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { isLovablePreview } from '@/lib/utils/environment';
import { getDisplayName } from '@/lib/utils/display-name';
import {
  people as dummyPeople,
  companies as dummyCompanies,
  deals as dummyDeals,
} from '@/data/dummy';

/**
 * Resolve a *display name* to a real entity with a real id.
 *
 * Several surfaces only carry a name at the point of a click — meeting
 * attendees, email senders, a deal's company label, a task's linked subjects.
 * They used to resolve those names against `@/data/dummy`, which works in
 * Lovable preview and silently fails in production: the lookup misses, so the
 * click either did nothing or (worse) opened a detail sheet with the display
 * name sitting in the id slot, which resolves to nothing server-side.
 *
 * This hook keeps the preview behaviour and adds the production one: the same
 * backend search queries the global search bar uses, narrowed to an exact
 * name match so a fuzzy hit never opens the wrong record.
 */

export interface ResolvedPerson {
  id: string;
  name: string;
  title?: string;
  company?: string;
  companyId?: string;
  email?: string;
  phone?: string;
}

export interface ResolvedCompany {
  id: string;
  name: string;
  domain?: string;
  industry?: string;
  description?: string;
  location?: string;
  website?: string;
}

export interface ResolvedDeal {
  id: string;
  name: string;
  company?: string;
  amount?: number;
}

/** Names come from mixed sources (calendar, CRM, email headers) — compare loosely. */
const normalize = (value: string) => value.trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Pick the entry whose name matches exactly. The backend `search` filter is
 * fuzzy and ranked, so taking results[0] would happily return "Jon Smithers"
 * for "Jon Smith" and open the wrong person's sheet. No exact match -> null,
 * and callers leave the name inert rather than guessing.
 */
function exactMatch<T>(candidates: T[], name: string, nameOf: (item: T) => string): T | null {
  const target = normalize(name);
  return candidates.find(c => normalize(nameOf(c)) === target) ?? null;
}

export const useEntityByName = () => {
  const workspaceId = useWorkspaceId();
  const [searchPeople] = useSearchPeopleLazyQuery();
  const [searchCompanies] = useSearchCompaniesLazyQuery();
  const [searchDeals] = useSearchDealsLazyQuery();

  const resolvePerson = useCallback(
    async (name: string): Promise<ResolvedPerson | null> => {
      if (!name?.trim()) return null;

      if (isLovablePreview()) {
        const p = exactMatch(dummyPeople, name, d => d.name);
        return p
          ? { id: p.id, name: p.name, title: p.title, company: p.company, email: p.email, phone: p.phone }
          : null;
      }

      if (!workspaceId) return null;
      try {
        const { data } = await searchPeople({
          variables: { workspaceId: String(workspaceId), search: name.trim(), first: 10 },
        });
        const nodes = (data?.contacts?.edges ?? []).map(e => e?.node).filter(Boolean);
        const hit = exactMatch(
          nodes,
          name,
          c => getDisplayName(c!.properties?.firstName, c!.properties?.lastName, c!.primaryEmail) || c!.primaryEmail || '',
        );
        if (!hit) return null;
        const account = hit.accounts?.edges?.[0]?.node;
        return {
          id: hit.id,
          name:
            getDisplayName(hit.properties?.firstName, hit.properties?.lastName, hit.primaryEmail)
            || hit.primaryEmail
            || name,
          title: hit.properties?.jobTitle || undefined,
          company: account?.properties?.name || undefined,
          companyId: account?.id || undefined,
          email: hit.primaryEmail || undefined,
        };
      } catch (error) {
        console.error('Failed to resolve person by name:', error);
        return null;
      }
    },
    [workspaceId, searchPeople],
  );

  const resolveCompany = useCallback(
    async (name: string): Promise<ResolvedCompany | null> => {
      if (!name?.trim()) return null;

      if (isLovablePreview()) {
        const c = exactMatch(dummyCompanies, name, d => d.name);
        return c
          ? {
              id: c.id,
              name: c.name,
              industry: (c as { industry?: string }).industry,
              description: (c as { description?: string }).description,
              location: (c as { location?: string }).location,
              website: (c as { website?: string }).website,
            }
          : null;
      }

      if (!workspaceId) return null;
      try {
        const { data } = await searchCompanies({
          variables: { workspaceId: String(workspaceId), search: name.trim(), first: 10 },
        });
        const nodes = (data?.accounts?.edges ?? []).map(e => e?.node).filter(Boolean);
        const hit = exactMatch(nodes, name, a => a!.properties?.name || '');
        if (!hit) return null;
        return {
          id: hit.id,
          name: hit.properties?.name || name,
          domain: hit.domain || undefined,
          website: hit.domain || undefined,
        };
      } catch (error) {
        console.error('Failed to resolve company by name:', error);
        return null;
      }
    },
    [workspaceId, searchCompanies],
  );

  const resolveDeal = useCallback(
    async (name: string): Promise<ResolvedDeal | null> => {
      if (!name?.trim()) return null;

      if (isLovablePreview()) {
        const d = exactMatch(dummyDeals, name, x => x.name);
        return d ? { id: d.id, name: d.name, company: d.company } : null;
      }

      if (!workspaceId) return null;
      try {
        const { data } = await searchDeals({
          variables: { workspaceId: String(workspaceId), search: name.trim(), first: 10 },
        });
        const nodes = (data?.deals?.edges ?? []).map(e => e?.node).filter(Boolean);
        const hit = exactMatch(nodes, name, d => d!.properties?.name || '');
        if (!hit) return null;
        return {
          id: hit.id,
          name: hit.properties?.name || name,
          company: hit.accounts?.edges?.[0]?.node?.properties?.name || undefined,
          amount: hit.properties?.amount != null ? Number(hit.properties.amount) : undefined,
        };
      } catch (error) {
        console.error('Failed to resolve deal by name:', error);
        return null;
      }
    },
    [workspaceId, searchDeals],
  );

  return { resolvePerson, resolveCompany, resolveDeal };
};
